/* eslint-disable @typescript-eslint/naming-convention -- request bodies and response types mirror API snake_case */
import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { DateTime } from 'luxon';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { ConfigurationService } from '@sondermind/configuration';
import { HttpService } from '@sondermind/http';
import {
  IAPIFetchResponse,
  IAPIServiceRequestOptions,
  IAPIListResponse,
  IAPIFetchRequestOptions
} from '@sondermind/utilities/models-http';

import { LivekitVideoToken } from '@sondermind/utilities/models-scheduling';

import { ScheduledEvent, ScheduledEventResponse } from '../models/scheduled-event';

@Injectable({
  providedIn: 'root'
})
export class ScheduledEventsHttpService {
  constructor(
    private configurationService: ConfigurationService,
    private http: HttpService,
  ) {}

  fetch(
    id: number | string,
    opts: Partial<IAPIFetchRequestOptions> = {}
  ): Observable<IAPIFetchResponse<ScheduledEvent>> {
    const url = `${this.configurationService.env.svcBase}/scheduling/scheduled_events/${id}`;
    const params = this.http.generateFetchParams(opts);
    return this.http.get<IAPIFetchResponse<ScheduledEventResponse>>(url, params).pipe(
      map((resp) => ({ ...resp, data: new ScheduledEvent(resp.data) })),
      this.http.handleHttpError(opts.error)
    );
  }

  livekitToken(
    id: number | string,
    opts: Partial<IAPIFetchRequestOptions> = {}
  ): Observable<IAPIFetchResponse<LivekitVideoToken>> {
    const url =
      `${this.configurationService.env.svcBase}/scheduling/scheduled_events/${id}/livekit_token?consent_ready=true`;
    const params = this.http.generateFetchParams(opts);

    return this.http.get<IAPIFetchResponse<LivekitVideoToken>>(url, params).pipe(
      map((resp) => ({ ...resp, data: LivekitVideoToken.fromJson(resp.data) })),
      this.http.handleHttpError(opts.error)
    );
  }

  directLivekitToken(
    linkId: string,
    opts: Partial<IAPIFetchRequestOptions> = {}
  ): Observable<IAPIFetchResponse<LivekitVideoToken>> {
    const url = `${this.configurationService.env.svcBase}/scheduling/direct_livekit_token`;
    const params = this.http.generateFetchParams(opts);
    return this.http.post<IAPIFetchResponse<LivekitVideoToken>>(url, { link_id: linkId }, params).pipe(
      map((resp) => ({ ...resp, data: LivekitVideoToken.fromJson(resp.data) })),
      // The registered HttpErrorService keeps only `errors` and drops the envelope's
      // top-level `codes`, which is exactly what the page branches on to tell a dead
      // link from a session that has not started yet. Capture them before handing the
      // failure on, under `fields.codes` where the platform's ApiError would carry them.
      catchError((err: HttpErrorResponse) => {
        const codes = err?.error?.codes;
        return codes
          ? throwError(() => Object.assign(new Error('direct link redeem failed'), {
            status: err.status,
            fields: { codes },
          }))
          : throwError(() => err);
      }),
      // A spent, expired, or unknown link answers 404, and that is a real outcome this
      // caller renders -- not "no such resource, carry on". null404 (on by default) would
      // turn it into an empty emission and leave the page waiting forever.
      this.http.handleHttpError({ null404: false, ...opts.error })
    );
  }

  postDirectJoinLink(
    id: number | string,
    ttl_seconds: number,
    send_to_client: boolean,
    opts: Partial<IAPIFetchRequestOptions> = {}
  ): Observable<IAPIFetchResponse<{ url: string }>> {
    const url = `${this.configurationService.env.svcBase}/scheduling/scheduled_events/${id}/direct_join_link`;
    const params = this.http.generateFetchParams(opts);
    return this.http.post<IAPIFetchResponse<{ url: string }>>(url, {
      ttl_seconds,
      send_to_client
    }, params).pipe(
      this.http.handleHttpError(opts.error)
    );
  }

  startLivekitEgress(id: number, opts: Partial<IAPIFetchRequestOptions> = {}): Observable<SMEgressResponse> {
    const url = `${this.configurationService.env.svcBase}/scheduling/livekit_egress`;
    const params = this.http.generateFetchParams(opts);

    return this.http
      .post<IAPIFetchResponse<SMEgressResponse>>(url, { calendar_event_id: id }, params)
      .pipe(
        map((res) => res?.data ?? null),
        this.http.handleHttpError()
      );
  }

  stopLivekitEgress(
    id: number,
    persona: string,
    opts: Partial<IAPIFetchRequestOptions> = {}
  ): Observable<SMEgressResponse> {
    const url =
      `${this.configurationService.env.svcBase}/scheduling/calendar_events/${id}/revoke_transcription_consent`;
    const params = this.http.generateFetchParams(opts);
    return this.http
      .patch<IAPIFetchResponse<SMEgressResponse>>(url, { initiated_by_persona: persona }, params)
      .pipe(
        map((res) => res?.data ?? null),
        this.http.handleHttpError()
      );
  }

  startLivekitMultiEgress(id: number, opts: Partial<IAPIFetchRequestOptions> = {}): Observable<SMMultiEgressResponse> {
    const url = `${this.configurationService.env.svcBase}/scheduling/livekit_recording/start`;
    const params = this.http.generateFetchParams(opts);

    return this.http
      .post<IAPIFetchResponse<SMMultiEgressResponse>>(url, { calendar_event_id: id }, params)
      .pipe(
        map((res) => res?.data ?? null),
        this.http.handleHttpError()
      );
  }

  stopLivekitMultiEgress(
    id: number,
    egressId?: string,
    opts: Partial<IAPIFetchRequestOptions> = {}
  ): Observable<SMMultiEgressResponse> {
    const url = `${this.configurationService.env.svcBase}/scheduling/livekit_recording/stop`;
    const params = this.http.generateFetchParams(opts);
    const body = egressId
      ? { calendar_event_id: id, egress_id: egressId }
      : { calendar_event_id: id };
    return this.http
      .post<IAPIFetchResponse<SMMultiEgressResponse>>(url, body, params)
      .pipe(
        map((res) => res?.data ?? null),
        this.http.handleHttpError()
      );
  }

  getStatusLivekitMultiEgress(
    id: number,
    opts: Partial<IAPIFetchRequestOptions> = {}
  ): Observable<SMMultiEgressResponse> {
    const url =
      `${this.configurationService.env.svcBase}/scheduling/livekit_recording/${id}/status`;
    const params = this.http.generateFetchParams(opts);
    return this.http
      .get<IAPIFetchResponse<SMMultiEgressResponse>>(url, params)
      .pipe(
        map((res) => res?.data ?? null),
        this.http.handleHttpError()
      );
  }

  update(
    eventId: string | number,
    updateParams: ScheduledEventUpdateParams,
    opts: Partial<IAPIServiceRequestOptions> = {}
  ): Observable<IAPIFetchResponse<ScheduledEvent>> {
    const params = this.http.generateServiceParams(opts);
    const url =
      `${this.configurationService.env.svcBase}/scheduling/scheduled_events/${eventId}`;

    return this.http
      .patch<IAPIFetchResponse<ScheduledEventResponse>>(url, { scheduled_event: updateParams }, params)
      .pipe(
        map((resp) => ({ ...resp, data: new ScheduledEvent(resp.data) })),
        this.http.handleHttpError(opts.error)
      );
  }

  cancel$(eventId: string | number, notifyCancellation: boolean = false): Observable<ScheduledEvent> {
    return this.http.post<IAPIFetchResponse<ScheduledEventResponse>>(
      `${this.configurationService.env.svcBase}/scheduling/scheduled_events/${eventId}/cancel`,
      { scheduled_event: { notify_about_cancellation: notifyCancellation } }
    ).pipe(
      map((resp) => new ScheduledEvent(resp?.data)),
      this.http.handleHttpError({})
    );
  }

  trace(eventId: string | number, joinedRoom: boolean = false, onBehalfOfClient: boolean = false): Observable<ScheduledEvent> {
    const payload: { activity_time: string; joined_room: boolean; on_behalf_of_client?: boolean } = {
      activity_time: DateTime.now().toISO({ includeOffset: true }),
      joined_room: joinedRoom
    };
    if (onBehalfOfClient) {
      payload.on_behalf_of_client = true;
    }
    return this.http.post<IAPIFetchResponse<ScheduledEventResponse>>(
      `${this.configurationService.env.svcBase}/scheduling/scheduled_events/${eventId}/trace`,
      payload
    ).pipe(
      map((resp) => new ScheduledEvent(resp?.data)),
      this.http.handleHttpError({})
    );
  }

  index(options: Partial<ScheduledEventsIndexOptions> = {}): Observable<ScheduledEvent[]> {
    const url = `${this.configurationService.env.svcBase}/scheduling/scheduled_events`;

    const params = new HttpParams()
      .append('view', 'with_questionnaires')
      .append('filter[for_date]', options.forDate.toISOString());

    return this.http
      .get<IAPIListResponse<ScheduledEventResponse>>(url, params)
      .pipe(
        map((res) => res?.data?.map((schedEvent) => new ScheduledEvent(schedEvent))),
        this.http.handleHttpError()
      );
  }

  getClaimStatus$(patientIds: number[]): Observable<ClaimStatusResponse[]> {
    return this.http
      .post<IAPIListResponse<ClaimStatusResponse>>(
        `${this.configurationService.env.svcBase}/scheduling/patients/check-claim-ready`,
        { patient_ids: patientIds }
      )
      .pipe(
      map((res) => res?.data),
      this.http.handleHttpError()
    );
  }
}

export type ScheduledEventUpdateParams = {
  session_type: string;
  title?: string;
  start_time: Date;
  end_time: Date;
  location_id: number;
  patient_id: number;
  notify_about_cancellation: boolean;
  session_duration_type_tag?: string;
};

export type ScheduledEventsIndexOptions = {
  userTimeZone: string;
  forDate: Date;
  view: boolean;
};

export interface SMEgressResponse {
  egress_id: string;
  room_id?: string;
  room_name: string;
  status: string;
}
export interface SMMultiEgressResponse {
  egress_id: string;
  active: boolean;
  calendar_event_id: number;
}
export interface ClaimStatusResponse {
  patient_id: number;
  blocker_priority: number;
  blocker_message: string;
}
